import { APP_INITIALIZER, Inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { AllSearchResultsFilterablesGQL } from 'src/app/app.query.generated';
import { Locale, Qualification, SharryKind, SharrySelection } from 'src/types/graphql';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';

@Injectable({ providedIn: 'root' })
export class SearchResultsFilterablesService {
  filterables: {
    tour: Partial<SharryKind>[];
    experience: Partial<SharryKind>[];
    item: Partial<SharryKind>[];
    rental: Partial<SharryKind>[];
    accommodation: Partial<SharryKind>[];
    sharry: Partial<SharryKind>[];
    wonder: Partial<SharryKind>[];
    event: Partial<SharryKind>[];
    users: Partial<Qualification>[];
  };
  filterablesInline: Partial<SharryKind>[];
  userFilterableIds: string[];
  sharryFilterableIds: string[];
  tourFilterableIds: string[];
  experienceFilterableIds: string[];
  itemFilterableIds: string[];
  rentalFilterableIds: string[];
  accommodationFilterableIds: string[];
  wonderFilterableIds: string[];
  eventFilterableIds: string[];
  wonderSelectable: SharrySelection[] = [
    SharrySelection.WithIndex,
    SharrySelection.WithItinerary,
    SharrySelection.Territory,
  ];
  eventSelectable: SharrySelection[] = [SharrySelection.SimpleEvent];
  sharrySelectable: SharrySelection[] = [
    SharrySelection.Tour,
    SharrySelection.Experience,
    SharrySelection.Item,
    SharrySelection.Accommodation,
    SharrySelection.Rental,
  ];

  currentLang: string = 'it';

  constructor(
    private allSearchResultsFilterablesGQL: AllSearchResultsFilterablesGQL,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
  ) {}

  init(lang?: string): Observable<boolean> {
    this.currentLang = lang ?? this.translateService.language ?? 'it';

    return this.allSearchResultsFilterablesGQL.fetch({ locale: this.currentLang as Locale }).pipe(
      map(r => ({
        userFilterables: r.data.allQualifications,
        contentFilterables: r.data.allSharryKind,
      })),
      tap(({ userFilterables, contentFilterables }) => {
        this.filterablesInline = [...(contentFilterables as SharryKind[])];

        const contents = contentFilterables.reduce<{
          tour?: Partial<SharryKind>[];
          experience?: Partial<SharryKind>[];
          item?: Partial<SharryKind>[];
          rental?: Partial<SharryKind>[];
          accommodation?: Partial<SharryKind>[];
          sharry?: Partial<SharryKind>[];
          wonder?: Partial<SharryKind>[];
          event?: Partial<SharryKind>[];
        }>((acc, current) => {
          if (current.contentType === 'sharry') {
            if (acc[current.contentType as 'sharry']) {
              acc[current.contentType as 'sharry']!.push(current as SharryKind);
            } else {
              acc[current.contentType as 'sharry'] = [current as SharryKind];
            }
            const selection = current.sharrySelectableKind?.sharrySelection;
            if (selection) {
              if (acc[selection as 'item']) {
                acc[selection as 'item']!.push(current as SharryKind);
              } else {
                acc[selection as 'item'] = [current as SharryKind];
              }
            }
          } else {
            if (acc[current.contentType as 'wonder']) {
              acc[current.contentType as 'wonder']!.push(current as SharryKind);
            } else {
              acc[current.contentType as 'wonder'] = [current as SharryKind];
            }
          }
          return acc;
        }, {});
        this.filterables = {
          users: userFilterables,
          event: contents.event,
          wonder: contents.wonder,
          sharry: contents.sharry,
          tour: contents.tour,
          experience: contents.experience,
          item: contents.item,
          rental: contents.rental,
          accommodation: contents.accommodation,
        };
        this.tourFilterableIds = [...(contents.tour || [])].map(f => f.id);
        this.experienceFilterableIds = [...(contents.experience || [])].map(f => f.id);
        this.itemFilterableIds = [...(contents.item || [])].map(f => f.id);
        this.rentalFilterableIds = [...(contents.rental || [])].map(f => f.id);
        this.accommodationFilterableIds = [...(contents.accommodation || [])].map(f => f.id);

        this.sharryFilterableIds = [...(contents.sharry || [])].map(f => f.id);
        this.wonderFilterableIds = [...(contents.wonder || [])].map(f => f.id);
        this.eventFilterableIds = [...(contents.event || [])].map(f => f.id);

        this.userFilterableIds = [...userFilterables].map(f => f.id);
      }),
      map(() => true),
    );
  }
}
const initFilterablesFactory =
  (searchResultsFilterablesService: SearchResultsFilterablesService, i18n: ITranslationService) =>
  (): Observable<boolean> => {
    const immediateLang = i18n.language;
    if (immediateLang) {
      return searchResultsFilterablesService.init(immediateLang);
    }
    return i18n.events.languageChanged.pipe(
      filter((l: any) => !!l),
      take(1),
      switchMap((l: string) => searchResultsFilterablesService.init(l)),
    );
  };

export const INIT_FILTERABLES_PROVIDER = {
  provide: APP_INITIALIZER,
  useFactory: initFilterablesFactory,
  multi: true,
  deps: [SearchResultsFilterablesService, I18NEXT_SERVICE],
};
